import { push as pushRoute } from 'react-router-redux';
import { delay } from 'redux-saga';
import { take, call, put, fork, cancel } from 'redux-saga/effects';

import {
  SIGNIN,
  SIGNUP,
  SIGNIN_ERROR,
  SIGNOUT,
  UPDATE_TOKEN,
  CLEAR_LOCAL_SESSION,
} from 'actions/constants';
import refreshSession from 'lib/refreshSession';
import sessionsDestroy from 'fetchers/sessions/destroy';
import AuthenticationActions from 'actions/communication/authentication';
import DataStoreActions from 'actions/dataStore';

import AuthData from 'lib/authData';
import { clearSessionMarker } from 'lib/sessionMarker';
import { bumpSessionGeneration } from 'lib/sessionGeneration';
import sendUserIdentify from 'lib/gtm/userIdentify';

/** How often the session is renewed while the tab stays open. */
export const REFRESH_INTERVAL_MS = 3600000; // 1 hour

/**
 * The probe and the refresh timer handle their own failures. Without this, the
 * globally configured `onError` navigates on the way past (`/unauthorized`, or
 * `/signout` then `/signin`), which races the deliberate signout below.
 */
const rethrow = { onError: (error: unknown) => Promise.reject(error) };

const isNoSession = (error: unknown): boolean =>
  ['UnauthorizedException', 'AuthTokenExpiredException'].includes(
    (error as { name?: string })?.name ?? ''
  );

/**
 * CD-8740 — renew the session every hour.
 *
 * `PUT /sessions` carries no body and reads no localStorage: the refresh token
 * travels in the HttpOnly `access_refresh` cookie. The response re-sets all
 * three cookies, the `has_session` marker included — a renewal that let the
 * marker lapse would be a silent logout on the next cold load.
 *
 * Via `refreshSession`, not a bare `sessionsUpdate`: refresh is a single-use
 * rotation, and every open tab runs its own copy of this loop. The wrapper
 * serialises concurrent refreshes cross-tab so one tab's renewal cannot 401
 * another's — a 401 here is treated as "session gone" and signs the user out.
 */
export function* updateToken(): Generator {
  while (true) {
    yield delay(REFRESH_INTERVAL_MS);

    try {
      const result = yield call(refreshSession, rethrow);
      AuthData.store(result);
    } catch (error) {
      // AC7 — a 401 here means the session is genuinely gone, so take the same
      // path as an explicit logout rather than failing silently or retrying
      // forever.
      if (isNoSession(error)) {
        yield put(AuthenticationActions.signOut());
        return;
      }
      // Anything else is a transient failure (offline, 5xx). Fall through and
      // try again at the next interval rather than signing the user out over a
      // dropped connection.
      console.debug('Session refresh failed, will retry:', error);
    }
  }
}

function* loginFlow(): Generator {
  let updateTokenTask: any = null;

  // At most one refresh loop, ever. A second loop would double every hourly
  // refresh, and refresh is a single-use rotation.
  function* stopRefreshLoop(): Generator {
    if (updateTokenTask) {
      yield cancel(updateTokenTask);
      updateTokenTask = null;
    }
  }

  while (true) {
    const action: any = yield take([
      SIGNIN,
      SIGNUP,
      SIGNOUT,
      SIGNIN_ERROR,
      UPDATE_TOKEN,
      CLEAR_LOCAL_SESSION,
    ]);

    switch (action.type) {
      case SIGNUP:
      case SIGNIN:
        bumpSessionGeneration();
        yield* stopRefreshLoop();
        updateTokenTask = yield fork(updateToken);

        sendUserIdentify();

        break;
      case SIGNOUT:
        bumpSessionGeneration();
        yield* stopRefreshLoop();

        try {
          // No body: oso-api identifies the session from the `access_refresh`
          // cookie and responds with deletions for every session cookie.
          yield call(sessionsDestroy, rethrow);
        } catch (error) {
          // Best effort — the browser must end up signed out either way.
          console.debug('Failed to destroy session:', error);
        }

        AuthData.drop();
        // The server's response deletes the marker, but a failed or blocked
        // DELETE must not leave it behind pointing at a session that is gone.
        clearSessionMarker();

        yield put(DataStoreActions.dropAll());
        yield put(pushRoute(action.redirectTo || '/'));

        break;
      case CLEAR_LOCAL_SESSION:
        // The referral hand-over (CaseLookup): the server has already replaced
        // the standard cookies, and SIGNOUT's DELETE /sessions would expire the
        // `referral` cookie just minted. Forget the standard session here only.
        // Deliberately no `dropAll` (the referral journey keeps state in the
        // data store) and no navigation (the customer is mid-journey).
        bumpSessionGeneration();
        yield* stopRefreshLoop();
        AuthData.drop();
        clearSessionMarker();

        break;
      case UPDATE_TOKEN:
        yield* stopRefreshLoop();
        updateTokenTask = yield fork(updateToken);

        break;
      default:
        break;
    }
  }
}

export default loginFlow;
